import { supabase, supabaseAdmin } from "../lib/supabaseClient";

const adminClient = supabaseAdmin ?? supabase;
const ELIGIBLE_REWARD_SIZE_SELECT = `
  id,
  name,
  size_code,
  dilim_points,
  is_available,
  products (
    id,
    name,
    product_type,
    category,
    is_available
  )
`;
const ELIGIBLE_REWARD_SIZE_SELECT_FALLBACK = `
  id,
  name,
  size_code,
  is_available,
  products (
    id,
    name,
    product_type,
    category,
    is_available
  )
`;

const isMissingProductSizeDilimPointsColumn = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const queryError = error as { code?: string; message?: string };
  return queryError.code === '42703' && (queryError.message ?? '').includes('product_sizes.dilim_points');
};

type RewardGroupRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type MilestoneStepRow = {
  step_number: number;
  label: string | null;
  reward_group_id: string | null;
  created_at: string;
  updated_at: string;
  dilim_reward_groups?: RewardGroupRow | RewardGroupRow[] | null;
};

type ProductRelationRow = {
  id: string;
  name: string;
  product_type: string | null;
  category: string | null;
  is_available?: boolean | null;
};

type RewardGroupItemRow = {
  id: string;
  reward_group_id: string;
  product_size_id: string;
  selection_weight: number;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  product_sizes?: {
    id: string;
    name: string;
    size_code: string | null;
    dilim_points: number | null;
    products?: ProductRelationRow | ProductRelationRow[] | null;
  } | Array<{
    id: string;
    name: string;
    size_code: string | null;
    dilim_points: number | null;
    products?: ProductRelationRow | ProductRelationRow[] | null;
  }> | null;
};

type EligibleRewardSizeRow = {
  id: string;
  name: string;
  size_code: string | null;
  dilim_points: number | null;
  is_available: boolean;
  products?: {
    id: string;
    name: string;
    product_type: string | null;
    category: string | null;
    is_available: boolean | null;
  } | Array<{
    id: string;
    name: string;
    product_type: string | null;
    category: string | null;
    is_available: boolean | null;
  }> | null;
};

type DilimPointProductSizeRow = {
  id: string;
  product_id: string;
  name: string;
  size_code: string | null;
  dilim_points: number | null;
  is_available: boolean;
  is_default: boolean;
  display_order: number;
  products?: ProductRelationRow | ProductRelationRow[] | null;
};

export interface DilimRewardGroupRecord {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DilimMilestoneStepRecord {
  stepNumber: number;
  label: string | null;
  rewardGroupId: string | null;
  rewardGroupName: string | null;
  rewardGroupSlug: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DilimRewardGroupItemRecord {
  id: string;
  rewardGroupId: string;
  productSizeId: string;
  productId: string | null;
  productName: string | null;
  productType: string | null;
  productCategory: string | null;
  sizeName: string | null;
  sizeCode: string | null;
  dilimPoints: number;
  selectionWeight: number;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EligibleDilimRewardSize {
  id: string;
  productId: string | null;
  productName: string | null;
  productType: string | null;
  productCategory: string | null;
  sizeName: string;
  sizeCode: string | null;
  dilimPoints: number;
  isAvailable: boolean;
}

export interface DilimPointProductSizeRecord {
  id: string;
  productId: string;
  productName: string;
  productType: string | null;
  productCategory: string | null;
  productIsAvailable: boolean;
  sizeName: string;
  sizeCode: string | null;
  dilimPoints: number;
  isAvailable: boolean;
  isDefault: boolean;
  displayOrder: number;
}

const mapRewardGroup = (row: RewardGroupRow): DilimRewardGroupRecord => ({
  id: row.id,
  name: row.name,
  slug: row.slug,
  description: row.description,
  isActive: Boolean(row.is_active),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const takeFirst = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value ?? null;
};

const mapMilestoneStep = (row: MilestoneStepRow): DilimMilestoneStepRecord => {
  const rewardGroup = takeFirst(row.dilim_reward_groups);

  return {
    stepNumber: Number(row.step_number),
    label: row.label,
    rewardGroupId: row.reward_group_id,
    rewardGroupName: rewardGroup?.name ?? null,
    rewardGroupSlug: rewardGroup?.slug ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const mapRewardGroupItem = (row: RewardGroupItemRow): DilimRewardGroupItemRecord => {
  const productSize = takeFirst(row.product_sizes);
  const product = takeFirst(productSize?.products);

  return {
    id: row.id,
    rewardGroupId: row.reward_group_id,
    productSizeId: row.product_size_id,
    productId: product?.id ?? null,
    productName: product?.name ?? null,
    productType: product?.product_type ?? null,
    productCategory: product?.category ?? null,
    sizeName: productSize?.name ?? null,
    sizeCode: productSize?.size_code ?? null,
    dilimPoints: Number(productSize?.dilim_points ?? 0),
    selectionWeight: Number(row.selection_weight ?? 1),
    displayOrder: Number(row.display_order ?? 0),
    isActive: Boolean(row.is_active),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const mapEligibleRewardSize = (row: EligibleRewardSizeRow): EligibleDilimRewardSize => {
  const product = takeFirst(row.products);

  return {
    id: row.id,
    productId: product?.id ?? null,
    productName: product?.name ?? null,
    productType: product?.product_type ?? null,
    productCategory: product?.category ?? null,
    sizeName: row.name,
    sizeCode: row.size_code,
    dilimPoints: Number(row.dilim_points ?? 0),
    isAvailable: Boolean(row.is_available && product?.is_available !== false),
  };
};

const mapDilimPointProductSize = (row: DilimPointProductSizeRow): DilimPointProductSizeRecord => {
  const product = takeFirst(row.products);

  return {
    id: row.id,
    productId: row.product_id,
    productName: product?.name ?? 'Ürün',
    productType: product?.product_type ?? null,
    productCategory: product?.category ?? null,
    productIsAvailable: product?.is_available !== false,
    sizeName: row.name,
    sizeCode: row.size_code,
    dilimPoints: Number(row.dilim_points ?? 0),
    isAvailable: Boolean(row.is_available),
    isDefault: Boolean(row.is_default),
    displayOrder: Number(row.display_order ?? 0),
  };
};

const sortDilimPointProductSizes = (rows: DilimPointProductSizeRecord[]) =>
  [...rows].sort((a, b) => {
    const categoryCompare = (a.productCategory ?? '').localeCompare(b.productCategory ?? '', 'tr');
    if (categoryCompare !== 0) {
      return categoryCompare;
    }

    const productCompare = a.productName.localeCompare(b.productName, 'tr');
    if (productCompare !== 0) {
      return productCompare;
    }

    return a.displayOrder - b.displayOrder;
  });

export async function listDilimMilestoneSteps(): Promise<DilimMilestoneStepRecord[]> {
  const { data, error } = await adminClient
    .from("dilim_milestone_steps")
    .select(
      `
        step_number,
        label,
        reward_group_id,
        created_at,
        updated_at,
        dilim_reward_groups (
          id,
          name,
          slug,
          description,
          is_active,
          created_at,
          updated_at
        )
      `,
    )
    .order("step_number", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => mapMilestoneStep(row as MilestoneStepRow));
}

export interface UpsertDilimMilestoneStepInput {
  stepNumber: number;
  label?: string | null;
  rewardGroupId?: string | null;
}

export async function upsertDilimMilestoneStep(input: UpsertDilimMilestoneStepInput) {
  const { data, error } = await adminClient
    .from("dilim_milestone_steps")
    .upsert(
      [
        {
          step_number: input.stepNumber,
          label: input.label ?? null,
          reward_group_id: input.rewardGroupId ?? null,
        },
      ],
      { onConflict: "step_number" },
    )
    .select(
      `
        step_number,
        label,
        reward_group_id,
        created_at,
        updated_at,
        dilim_reward_groups (
          id,
          name,
          slug,
          description,
          is_active,
          created_at,
          updated_at
        )
      `,
    )
    .single();

  if (error) {
    throw error;
  }

  return mapMilestoneStep(data as MilestoneStepRow);
}

export async function listDilimRewardGroups(): Promise<DilimRewardGroupRecord[]> {
  const { data, error } = await adminClient
    .from("dilim_reward_groups")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => mapRewardGroup(row as RewardGroupRow));
}

export interface UpsertDilimRewardGroupInput {
  id?: string;
  name: string;
  slug: string;
  description?: string | null;
  isActive?: boolean;
}

export async function upsertDilimRewardGroup(input: UpsertDilimRewardGroupInput) {
  const { data, error } = await adminClient
    .from("dilim_reward_groups")
    .upsert(
      [
        {
          id: input.id,
          name: input.name,
          slug: input.slug,
          description: input.description ?? null,
          is_active: input.isActive ?? true,
        },
      ],
      { onConflict: "id" },
    )
    .select()
    .single();

  if (error) {
    throw error;
  }

  return mapRewardGroup(data as RewardGroupRow);
}

export async function deleteDilimRewardGroup(groupId: string) {
  const { error } = await adminClient.from("dilim_reward_groups").delete().eq("id", groupId);

  if (error) {
    throw error;
  }
}

export async function listDilimRewardGroupItems(
  rewardGroupId?: string | null,
): Promise<DilimRewardGroupItemRecord[]> {
  let query = adminClient
    .from("dilim_reward_group_items")
    .select(
      `
        id,
        reward_group_id,
        product_size_id,
        selection_weight,
        display_order,
        is_active,
        created_at,
        updated_at,
        product_sizes (
          id,
          name,
          size_code,
          dilim_points,
          products (
            id,
            name,
            product_type,
            category
          )
        )
      `,
    )
    .order("display_order", { ascending: true })
    .order("created_at", { ascending: true });

  if (rewardGroupId) {
    query = query.eq("reward_group_id", rewardGroupId);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => mapRewardGroupItem(row as RewardGroupItemRow));
}

export interface UpsertDilimRewardGroupItemInput {
  id?: string;
  rewardGroupId: string;
  productSizeId: string;
  selectionWeight?: number;
  displayOrder?: number;
  isActive?: boolean;
}

export async function upsertDilimRewardGroupItem(input: UpsertDilimRewardGroupItemInput) {
  const payload = {
    reward_group_id: input.rewardGroupId,
    product_size_id: input.productSizeId,
    selection_weight: input.selectionWeight ?? 1,
    display_order: input.displayOrder ?? 0,
    is_active: input.isActive ?? true,
  };

  const query = input.id
    ? adminClient
        .from("dilim_reward_group_items")
        .update(payload)
        .eq("id", input.id)
    : adminClient
        .from("dilim_reward_group_items")
        .insert(payload);

  const { data, error } = await query
    .select(
      `
        id,
        reward_group_id,
        product_size_id,
        selection_weight,
        display_order,
        is_active,
        created_at,
        updated_at,
        product_sizes (
          id,
          name,
          size_code,
          dilim_points,
          products (
            id,
            name,
            product_type,
            category
          )
        )
      `,
    )
    .single();

  if (error) {
    throw error;
  }

  return mapRewardGroupItem(data as RewardGroupItemRow);
}

export async function deleteDilimRewardGroupItem(itemId: string) {
  const { error } = await adminClient.from("dilim_reward_group_items").delete().eq("id", itemId);

  if (error) {
    throw error;
  }
}

export async function listEligibleDilimRewardSizes(): Promise<EligibleDilimRewardSize[]> {
  const baseQuery = adminClient
    .from("product_sizes")
    .select(ELIGIBLE_REWARD_SIZE_SELECT)
    .order("name", { ascending: true });

  const { data, error } = await baseQuery;

  if (isMissingProductSizeDilimPointsColumn(error)) {
    const fallbackResult = await adminClient
      .from("product_sizes")
      .select(ELIGIBLE_REWARD_SIZE_SELECT_FALLBACK)
      .order("name", { ascending: true });

    if (fallbackResult.error) {
      throw fallbackResult.error;
    }

    return (fallbackResult.data ?? []).map((row) => mapEligibleRewardSize(row as EligibleRewardSizeRow));
  }

  if (error) {
    throw error;
  }

  return (data ?? []).map((row) => mapEligibleRewardSize(row as EligibleRewardSizeRow));
}

export async function listDilimPointProductSizes(): Promise<DilimPointProductSizeRecord[]> {
  const { data, error } = await adminClient
    .from("product_sizes")
    .select(
      `
        id,
        product_id,
        name,
        size_code,
        dilim_points,
        is_available,
        is_default,
        display_order,
        products (
          id,
          name,
          product_type,
          category,
          is_available
        )
      `,
    )
    .order("display_order", { ascending: true });

  if (error) {
    throw error;
  }

  return sortDilimPointProductSizes(
    (data ?? []).map((row) => mapDilimPointProductSize(row as unknown as DilimPointProductSizeRow)),
  );
}

export interface UpdateDilimPointProductSizeInput {
  id: string;
  dilimPoints: number;
}

export async function updateDilimPointProductSizes(
  updates: UpdateDilimPointProductSizeInput[],
): Promise<DilimPointProductSizeRecord[]> {
  if (updates.length === 0) {
    return [];
  }

  const results = await Promise.all(
    updates.map(async (update) => {
      const { data, error } = await adminClient
        .from("product_sizes")
        .update({ dilim_points: Math.max(0, Math.floor(update.dilimPoints)) })
        .eq("id", update.id)
        .select(
          `
            id,
            product_id,
            name,
            size_code,
            dilim_points,
            is_available,
            is_default,
            display_order,
            products (
              id,
              name,
              product_type,
              category,
              is_available
            )
          `,
        )
        .single();

      if (error) {
        throw error;
      }

      return mapDilimPointProductSize(data as unknown as DilimPointProductSizeRow);
    }),
  );

  return sortDilimPointProductSizes(results);
}
